import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import styles from './DistanceSalesContract.module.css';

interface DistanceSalesContractProps {
  tierName: string;
  period: 'monthly' | 'yearly' | 'annual';
  onClose: () => void;
  billingName?: string;
  billingAddress?: string;
}

export function DistanceSalesContract({ tierName, period, onClose, billingName, billingAddress }: DistanceSalesContractProps) {
  const { language } = useLanguage();
  const { user } = useAuth();

  const isAnnual = period === 'annual' || period === 'yearly';
  const periodText = language === 'tr'
    ? (isAnnual ? 'Yıllık' : 'Aylık')
    : (isAnnual ? 'Annual' : 'Monthly');

  const buyerName = billingName?.trim() || user?.name || '_______________';
  const buyerEmail = user?.email || '_______________';
  const buyerAddress = billingAddress?.trim() || undefined;

  if (language === 'en') {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
          <div className={styles.content}>
            <h1 className={styles.title}>Distance Sales Agreement</h1>

            <section className={styles.section}>
              <h2>1. PARTIES</h2>
              <p>This Agreement is signed between the following parties within the framework of the terms and conditions specified below.</p>

              <h3>BUYER:</h3>
              <p><strong>Name:</strong> {buyerName}</p>
              <p><strong>Email:</strong> {buyerEmail}</p>
              {buyerAddress && <p><strong>Address:</strong> {buyerAddress}</p>}

              <h3>SELLER:</h3>
              <p><strong>Name:</strong> Sakıp Emre Öztürk</p>
              <p><strong>Company Type:</strong> Sole Proprietorship (Şahıs Şirketi)</p>
              <p><strong>Trade Name:</strong> PolysanSoft</p>
              <p><strong>Address:</strong> Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri, Turkey</p>
              <p><strong>Phone:</strong> +90 532 169 0938</p>
              <p><strong>Email:</strong> info@polysansoft.com</p>

              <p>By accepting this agreement, the BUYER acknowledges and agrees in advance that upon confirming the order subject to the contract, they will be obligated to pay the order price and any additional charges such as taxes, and that they have been informed about this matter.</p>
            </section>

            <section className={styles.section}>
              <h2>2. DEFINITIONS</h2>
              <p>In the application and interpretation of this agreement, the following terms shall have the meanings written next to them:</p>
              <ul>
                <li><strong>LAW:</strong> Consumer Protection Law No. 6502</li>
                <li><strong>REGULATION:</strong> Distance Contracts Regulation</li>
                <li><strong>SERVICE:</strong> Any consumer transaction other than the provision of goods performed or promised in exchange for a fee or benefit</li>
                <li><strong>SELLER:</strong> The company that offers or acts on behalf of the consumer as part of its commercial or professional activities</li>
                <li><strong>BUYER:</strong> A real or legal person who acquires, uses, or benefits from a good or service for non-commercial or non-professional purposes</li>
                <li><strong>SITE:</strong> The website belonging to the SELLER (polaris.polysansoft.com)</li>
                <li><strong>PARTIES:</strong> SELLER and BUYER</li>
                <li><strong>AGREEMENT:</strong> This agreement between SELLER and BUYER</li>
                <li><strong>SERVICE/PRODUCT:</strong> Software subscription service (Polaris) and similar intangible digital services</li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>3. SUBJECT</h2>
              <p>This Agreement regulates the rights and obligations of the parties in accordance with the provisions of the Consumer Protection Law No. 6502 and the Regulation on Distance Contracts regarding the sale and delivery of the service whose characteristics and sales price are specified below, which the BUYER has ordered electronically on the SELLER's website.</p>
              <p>The prices listed and announced on the site are sales prices. The announced prices and promises are valid until updated and changed. Prices announced for a limited period are valid until the end of the specified period.</p>
            </section>

            <section className={styles.section}>
              <h2>4. SELLER INFORMATION</h2>
              <p><strong>Name:</strong> Sakıp Emre Öztürk</p>
              <p><strong>Company Type:</strong> Sole Proprietorship (Şahıs Şirketi)</p>
              <p><strong>Trade Name:</strong> PolysanSoft</p>
              <p><strong>Address:</strong> Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri, Turkey</p>
              <p><strong>Phone:</strong> +90 532 169 0938</p>
              <p><strong>Email:</strong> info@polysansoft.com</p>
              <p><strong>Website:</strong> polaris.polysansoft.com</p>
            </section>

            <section className={styles.section}>
              <h2>5. BUYER INFORMATION</h2>
              <p><strong>Name:</strong> {buyerName}</p>
              <p><strong>Email:</strong> {buyerEmail}</p>
              {buyerAddress && <p><strong>Address:</strong> {buyerAddress}</p>}
            </section>

            <section className={styles.section}>
              <h2>6. CONTRACT SUBJECT SERVICE INFORMATION</h2>
              <p>The basic features (type, quantity, brand/model) of the service are published on the SELLER's website. If a campaign is organized by the Seller, you can review the basic features of the relevant service during the campaign period.</p>

              <h3>6.1 Service Details</h3>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Service Description</th>
                    <th>Period</th>
                    <th>Total (VAT Included)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Polaris {tierName} Subscription</td>
                    <td>{periodText}</td>
                    <td>As displayed on the payment page</td>
                  </tr>
                </tbody>
              </table>

              <h3>6.2 Prices</h3>
              <p>Listed and announced prices on the site are sales prices. Announced prices and promises are valid until updated and changed.</p>
            </section>

            <section className={styles.section}>
              <h2>7. GENERAL PROVISIONS</h2>

              <h3>7.1 Information and Acceptance</h3>
              <p>The BUYER accepts, declares, and undertakes that they have read and are informed about the basic features of the contract subject service, the sales price, payment method, and preliminary information regarding delivery on the SELLER's website, and has given the necessary confirmation electronically.</p>

              <h3>7.2 Service Activation</h3>
              <p>The contract subject service will be activated immediately upon successful payment confirmation and will be accessible via the user's account on the platform.</p>
            </section>

            <section className={styles.section}>
              <h2>8. PAYMENT METHODS</h2>
              <p>All payments are processed securely via Garanti BBVA Virtual POS (Sanal POS) with 3D Secure verification. You will be redirected to your bank's 3D Secure page to verify your identity during the payment process.</p>
              <p>Your card information is encrypted in transit and not stored by us. All payment data is processed and protected by Garanti BBVA in compliance with PCI-DSS standards.</p>
            </section>

            <section className={styles.section}>
              <h2>9. PLAN CHANGES</h2>
              <p>Yes, you can change your plan at any time. Your remaining balance will be transferred to the new plan and your subscription period will be adjusted accordingly.</p>
            </section>

            <section className={styles.section}>
              <h2>10. CANCELLATION AND REFUND</h2>
              <p>Yes, you can cancel at any time. A refund will be provided for the remaining unused period of your subscription.</p>
            </section>

            <section className={styles.section}>
              <h2>11. RIGHT OF WITHDRAWAL</h2>
              <p>In the case of distance contracts related to digital content and software services that are provided online and start being used immediately after purchase, the BUYER acknowledges that they cannot exercise the right of withdrawal after the service has been activated.</p>
              <p>According to Article 15 of the Distance Contracts Regulation, the right of withdrawal cannot be used for contracts concerning the delivery of digital content delivered electronically (such as software, applications, games, music, video, text) if the performance has begun with the consumer's prior explicit approval and acknowledgment of the loss of the right of withdrawal.</p>
            </section>

            <section className={styles.section}>
              <h2>12. DEFAULT AND LEGAL CONSEQUENCES</h2>
              <p>If the BUYER defaults on payments made by credit card, the BUYER accepts, declares, and undertakes that they will pay interest within the framework of the credit card agreement between the cardholder and the bank and will be responsible to the bank.</p>
            </section>

            <section className={styles.section}>
              <h2>13. AUTHORIZED COURT</h2>
              <p>Complaints and objections arising from this agreement shall be made to the consumer arbitration committee or consumer court in the location of the consumer's residence or where the consumer transaction was made.</p>
            </section>

            <section className={styles.section}>
              <h2>14. VALIDITY</h2>
              <p>When the BUYER makes the payment for the order placed on the Site, they are deemed to have accepted all terms of this agreement.</p>
              <p className={styles.date}>Agreement Date: {new Date().toLocaleDateString('en-US')}</p>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // Turkish version
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        <div className={styles.content}>
          <h1 className={styles.title}>Mesafeli Satış Sözleşmesi</h1>

          <section className={styles.section}>
            <h2>1. TARAFLAR</h2>
            <p>İşbu Sözleşme aşağıdaki taraflar arasında aşağıda belirtilen hüküm ve şartlar çerçevesinde imzalanmıştır.</p>

            <h3>ALICI:</h3>
            <p><strong>Ad-Soyad:</strong> {buyerName}</p>
            <p><strong>E-posta:</strong> {buyerEmail}</p>
            {buyerAddress && <p><strong>Adres:</strong> {buyerAddress}</p>}

            <h3>SATICI:</h3>
            <p><strong>Ad-Soyad:</strong> Sakıp Emre Öztürk</p>
            <p><strong>Şirket Türü:</strong> Şahıs Şirketi</p>
            <p><strong>Ticari Unvan:</strong> PolysanSoft</p>
            <p><strong>Adres:</strong> Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri, Türkiye</p>
            <p><strong>Telefon:</strong> +90 532 169 0938</p>
            <p><strong>E-posta:</strong> info@polysansoft.com</p>

            <p>İşbu sözleşmeyi kabul etmekle ALICI, sözleşme konusu siparişi onayladığı takdirde sipariş konusu bedeli ve varsa vergi gibi belirtilen ek ücretleri ödeme yükümlülüğü altına gireceğini ve bu konuda bilgilendirildiğini peşinen kabul eder.</p>
          </section>

          <section className={styles.section}>
            <h2>2. TANIMLAR</h2>
            <p>İşbu sözleşmenin uygulanmasında ve yorumlanmasında aşağıda yazılı terimler karşılarındaki yazılı açıklamaları ifade edeceklerdir:</p>
            <ul>
              <li><strong>KANUN:</strong> 6502 sayılı Tüketicinin Korunması Hakkında Kanun</li>
              <li><strong>YÖNETMELİK:</strong> Mesafeli Sözleşmeler Yönetmeliği</li>
              <li><strong>HİZMET:</strong> Bir ücret veya menfaat karşılığında yapılan ya da yapılması taahhüt edilen mal sağlama dışındaki her türlü tüketici işlemi</li>
              <li><strong>SATICI:</strong> Ticari veya mesleki faaliyetleri kapsamında tüketiciye hizmet sunan veya hizmet sunan adına veya hesabına hareket eden şirket</li>
              <li><strong>ALICI:</strong> Bir mal veya hizmeti ticari veya mesleki olmayan amaçlarla edinen, kullanan veya yararlanan gerçek ya da tüzel kişi</li>
              <li><strong>SİTE:</strong> SATICI'ya ait internet sitesi (polaris.polysansoft.com)</li>
              <li><strong>TARAFLAR:</strong> SATICI ve ALICI</li>
              <li><strong>SÖZLEŞME:</strong> SATICI ve ALICI arasında akdedilen işbu sözleşme</li>
              <li><strong>HİZMET/ÜRÜN:</strong> Yazılım abonelik hizmeti (Polaris) ve benzeri gayri maddi dijital hizmetler</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. KONU</h2>
            <p>İşbu Sözleşme, ALICI'nın, SATICI'ya ait internet sitesi üzerinden elektronik ortamda siparişini verdiği aşağıda nitelikleri ve satış fiyatı belirtilen hizmetin satışı ve teslimatı ile ilgili olarak 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmelere Dair Yönetmelik hükümleri gereğince tarafların hak ve yükümlülüklerini düzenler.</p>
            <p>Listelenen ve sitede ilan edilen fiyatlar satış fiyatıdır. İlan edilen fiyatlar ve vaatler güncelleme yapılana ve değiştirilene kadar geçerlidir. Süreli olarak ilan edilen fiyatlar ise belirtilen süre sonuna kadar geçerlidir.</p>
          </section>

          <section className={styles.section}>
            <h2>4. SATICI BİLGİLERİ</h2>
            <p><strong>Ad-Soyad:</strong> Sakıp Emre Öztürk</p>
            <p><strong>Şirket Türü:</strong> Şahıs Şirketi</p>
            <p><strong>Ticari Unvan:</strong> PolysanSoft</p>
            <p><strong>Adres:</strong> Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri, Türkiye</p>
            <p><strong>Telefon:</strong> +90 532 169 0938</p>
            <p><strong>E-posta:</strong> info@polysansoft.com</p>
            <p><strong>Web Sitesi:</strong> polaris.polysansoft.com</p>
          </section>

          <section className={styles.section}>
            <h2>5. ALICI BİLGİLERİ</h2>
            <p><strong>Ad-Soyad:</strong> {buyerName}</p>
            <p><strong>E-posta:</strong> {buyerEmail}</p>
            {buyerAddress && <p><strong>Adres:</strong> {buyerAddress}</p>}
          </section>

          <section className={styles.section}>
            <h2>6. SÖZLEŞME KONUSU HİZMET BİLGİLERİ</h2>
            <p>Hizmetin temel özellikleri (türü, miktarı, markası/modeli) SATICI'ya ait internet sitesinde yayınlanmaktadır. Satıcı tarafından kampanya düzenlenmiş ise ilgili hizmetin temel özelliklerini kampanya süresince inceleyebilirsiniz.</p>

            <h3>6.1 Hizmet Detayları</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Hizmet Açıklaması</th>
                  <th>Periyot</th>
                  <th>Toplam (KDV Dahil)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Polaris {tierName} Aboneliği</td>
                  <td>{periodText}</td>
                  <td>Ödeme sayfasında görüntülenen tutar</td>
                </tr>
              </tbody>
            </table>

            <h3>6.2 Fiyatlar</h3>
            <p>Listelenen ve sitede ilan edilen fiyatlar satış fiyatıdır. İlan edilen fiyatlar ve vaatler güncelleme yapılana ve değiştirilene kadar geçerlidir.</p>
          </section>

          <section className={styles.section}>
            <h2>7. GENEL HÜKÜMLER</h2>

            <h3>7.1 Bilgilendirme ve Kabul</h3>
            <p>ALICI, SATICI'ya ait internet sitesinde sözleşme konusu hizmetin temel nitelikleri, satış fiyatı ve ödeme şekli ile teslimat/aktivasyona ilişkin ön bilgileri okuyup, bilgi sahibi olduğunu, elektronik ortamda gerekli teyidi verdiğini kabul, beyan ve taahhüt eder.</p>

            <h3>7.2 Hizmet Aktivasyonu</h3>
            <p>Sözleşme konusu hizmet, başarılı ödeme onayının ardından derhal aktif edilecek ve kullanıcının platform üzerindeki hesabı üzerinden erişilebilir olacaktır.</p>
          </section>

          <section className={styles.section}>
            <h2>8. ÖDEME YÖNTEMLERİ</h2>
            <p>Tüm ödemeler Garanti BBVA Sanal POS üzerinden 3D Secure doğrulaması ile güvenli şekilde işlenmektedir. Ödeme işlemi sırasında kimliğinizi doğrulamak için bankanızın 3D Secure sayfasına yönlendirileceksiniz.</p>
            <p>Kart bilgileriniz şifrelenerek gönderilmekte ve tarafımızda saklanmamaktadır. Tüm ödeme verileri Garanti BBVA tarafından PCI-DSS standartlarına uygun şekilde işlenir ve korunur.</p>
          </section>

          <section className={styles.section}>
            <h2>9. PLAN DEĞİŞİKLİĞİ</h2>
            <p>Evet, istediğiniz zaman plan değiştirebilirsiniz. Kalan bakiyeniz yeni plana aktarılır ve süreniz buna göre ayarlanır.</p>
          </section>

          <section className={styles.section}>
            <h2>10. İPTAL VE İADE</h2>
            <p>Evet, istediğiniz zaman iptal edebilirsiniz. Kalan süreniz için iade yapılır.</p>
          </section>

          <section className={styles.section}>
            <h2>11. CAYMA HAKKI</h2>
            <p>Çevrimiçi olarak sunulan ve satın alma sonrasında derhal kullanılmaya başlanan dijital içerik ve yazılım hizmetlerine ilişkin mesafeli sözleşmelerde, ALICI hizmet aktif edildikten sonra cayma hakkını kullanamayacağını kabul eder.</p>
            <p>Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesi uyarınca, elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayri maddi malların teslimine ilişkin sözleşmelerde, tüketicinin önceden açık onayı ve cayma hakkını kaybedeceğine ilişkin bildirim ile ifaya başlanmış ise cayma hakkı kullanılamaz.</p>
          </section>

          <section className={styles.section}>
            <h2>12. TEMERRÜT HALİ VE HUKUKİ SONUÇLARI</h2>
            <p>ALICI, ödeme işlemlerini kredi kartı ile yaptığı durumda temerrüde düştüğü takdirde, kart sahibi banka ile arasındaki kredi kartı sözleşmesi çerçevesinde faiz ödeyeceğini ve bankaya karşı sorumlu olacağını kabul, beyan ve taahhüt eder.</p>
          </section>

          <section className={styles.section}>
            <h2>13. YETKİLİ MAHKEME</h2>
            <p>İşbu sözleşmeden doğan uyuşmazlıklarda şikayet ve itirazlar, tüketicinin yerleşim yerinin bulunduğu veya tüketici işleminin yapıldığı yerdeki tüketici sorunları hakem heyetine veya tüketici mahkemesine yapılacaktır.</p>
          </section>

          <section className={styles.section}>
            <h2>14. YÜRÜRLÜK</h2>
            <p>ALICI, Site üzerinden verdiği siparişe ait ödemeyi gerçekleştirdiğinde işbu sözleşmenin tüm şartlarını kabul etmiş sayılır.</p>
            <p className={styles.date}>Sözleşme Tarihi: {new Date().toLocaleDateString('tr-TR')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
